import Box from '@material-ui/core/Box';
import { Button } from 'app/components/BasicButtons/Button';
import { Icon } from 'app/components/BasicIcons/FontAwesome';
import { Progress } from 'app/components/LoadingIndicator';
import { useEffectOnMount } from 'app/hooks/useEffectOnMount';
import { PageWrapper } from 'app/Layout/FrontendLayout/components/PageWrapper';
import { ActionRenderer } from 'app/Layout/FrontendLayout/components/PageWrapper/PageActions/ActionRender';
import { useLayoutSlice } from 'app/Layout/FrontendLayout/slice';
import {
  selectHasNotSavedChanges,
  selectContinueToLink,
  selectConfirmApproved,
  selectConfirmRejected,
  selectContinueState,
} from 'app/Layout/FrontendLayout/slice/selectors';
import { useAppSettingsSlice } from 'app/slice';
import { selectAuthenticatedUser, selectPublicUrl } from 'app/slice/selectors';
import { Roles } from 'app/slice/types';
import { push } from 'connected-react-router';
import { translations } from 'locales/translations';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { buildURL } from 'utils/url-utils';
import { SaveFormButton } from './Actions/UserAttributesActions';
import { useUserAttributesDetailsSlice } from './slice';
import {
  selectUserAttributesDetailsProcessing,
  selectCreateUserAttributesDetailsCompleted,
  selectUpdateUserAttributesDetailsCompleted,
  selectCreateUserAttributesDetails,
  selectUpdateUserAttributesDetails,
} from './slice/selectors';
import {
  UserAttributesDetailsQStringParameters,
  UserAttributesDetailsState,
} from './slice/types';
import { Beforeunload } from 'react-beforeunload';
import { UserAttributesForm } from './UserAttributesForm';
import { SidePanelContentProps } from 'app/Layout/FrontendLayout/slice/type';

export interface UserAttributesDetailsProps extends SidePanelContentProps {
  queryParams: UserAttributesDetailsQStringParameters;
}

export function UserAttributesDetails(
  props: UserAttributesDetailsProps,
): JSX.Element {
  //PROPS
  const { queryParams, useSidePanel } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  //PERMISSIONS
  const User = useSelector(selectAuthenticatedUser);
  const IsAdmin =
    User !== undefined && User.Roles.includes(Roles.Administrators);

  //SLICES
  const { actions } = useUserAttributesDetailsSlice();
  const { actions: layoutActions } = useLayoutSlice();
  const { actions: appSettingsActions } = useAppSettingsSlice();

  //SELECTORS
  const publicUrl = useSelector(selectPublicUrl);
  //====================================================================
  const hasUnsavedChanges = useSelector(selectHasNotSavedChanges, shallowEqual);
  const continueLink = useSelector(selectContinueToLink);
  const loading = useSelector(selectUserAttributesDetailsProcessing);
  //====================================================================
  const confirmRejected = useSelector(selectConfirmRejected);
  const confirmApproved = useSelector(selectConfirmApproved);
  const continueToOther = useSelector(selectContinueState);
  //====================================================================
  const requestCreateCompleted = useSelector(
    selectCreateUserAttributesDetailsCompleted,
  );
  const requestUpdateCompleted = useSelector(
    selectUpdateUserAttributesDetailsCompleted,
  );
  //====================================================================
  const createModel = useSelector(
    selectCreateUserAttributesDetails,
    shallowEqual,
  );
  const editModel = useSelector(
    selectUpdateUserAttributesDetails,
    shallowEqual,
  );
  const baseModel = createModel || editModel;
  //====================================================================

  //STATES
  const [edit, setEdit] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);
  const [requestCompleted, setRequestCompleted] = useState<boolean | undefined>(
    undefined,
  );

  //REF
  const submitFormRef = useRef<any>(null);

  //FUNCTIONS
  const bindSubmitForm = useCallback(submitForm => {
    submitFormRef.current = submitForm;
  }, []);

  const handleSubmitForm = (e: any) => {
    if (submitFormRef.current) {
      submitFormRef.current(e);
    }
  };

  const handleSaveClick = (e: any) => {
    handleSubmitForm(e);
  };

  const handleSubmit = useCallback(
    (values: UserAttributesDetailsState) => {
      setBusy(true);
      if (edit) {
        dispatch(
          actions.updateUserAttributesDetails({
            current: values,
            original: baseModel,
          }),
        );
      } else {
        let value = values as UserAttributesDetailsState;
        console.debug('Data: ', value);
        dispatch(actions.createUserAttributesDetails(values));
      }
    },
    [actions, baseModel, dispatch, edit],
  );

  const handleCancelClick = useCallback(() => {
    if (useSidePanel) {
      dispatch(layoutActions.resetSidePanel());
    } else {
      dispatch(push('/UserAttributes'));
    }
    if (edit) {
      dispatch(actions.resetUpdateUserAttributesDetailsState());
    } else {
      dispatch(actions.resetCreateUserAttributesDetailsState());
    }
  }, [actions, dispatch, edit, layoutActions, useSidePanel]);

  const handleClose = () => {
    if (hasUnsavedChanges) {
      dispatch(layoutActions.setConfirmOpen(true));
    } else {
      handleCancelClick();
    }
  };

  //USE EFFECT ON MOUNT
  useEffectOnMount(() => {
    if (
      queryParams.id !== undefined &&
      !isNaN(parseInt(queryParams.id || ''))
    ) {
      dispatch(actions.initUpdateUserAttributesDetails(queryParams));
    } else {
      dispatch(actions.initUserAttributesDetails(queryParams));
    }
    setBusy(false);
    setEdit(false);
    setRequestCompleted(undefined);
    return () => {};
  });

  //USE EFFECT
  useEffect(() => {
    let active = loading === false;
    if (active) {
      if (queryParams.id !== undefined && !isNaN(parseInt(queryParams.id))) {
        setEdit(true);
      }
      if (requestCreateCompleted === true || requestUpdateCompleted === true) {
        setRequestCompleted(true);
        setBusy(false);
      } else if (
        requestCreateCompleted === false ||
        requestUpdateCompleted === false
      ) {
        setRequestCompleted(false);
        setBusy(false);
      }
      if (requestUpdateCompleted === true || requestCreateCompleted === true) {
        handleCancelClick();
      }
    }
    return () => {
      active = false;
    };
  }, [
    handleCancelClick,
    loading,
    queryParams.id,
    requestCreateCompleted,
    requestUpdateCompleted,
  ]);

  //USE EFFECT 2
  useEffect(() => {
    if (confirmApproved) {
      if (edit) {
        dispatch(actions.resetUpdateUserAttributesDetailsState());
      } else {
        dispatch(actions.resetCreateUserAttributesDetailsState());
      }
      if (continueToOther.continueOnApprove) {
        dispatch(layoutActions.resetSidePanel());
        setTimeout(() => {
          dispatch(
            layoutActions.openSidePanel({
              type: continueToOther.pageType,
              props: continueToOther.pageProps,
              expanded: continueToOther.expanded,
            }),
          );
          dispatch(layoutActions.resetContinueState());
        }, 200);
      } else if (continueLink !== undefined) {
        dispatch(appSettingsActions.navigate(continueLink));
        dispatch(layoutActions.resetSidePanel());
      } else {
        dispatch(layoutActions.resetSidePanel());
      }
    }
    if (confirmRejected) {
      dispatch(layoutActions.resetConfirmState());
    }
    return () => undefined;
  }, [
    actions,
    appSettingsActions,
    confirmApproved,
    confirmRejected,
    continueLink,
    continueToOther,
    dispatch,
    edit,
    layoutActions,
  ]);

  //LEFT ACTIONS
  const leftActions = [
    () => (
      <Fragment>
        <SaveFormButton
          size="small"
          startIcon={<Icon icon="save" />}
          onClick={handleSaveClick}
          disabled={busy}
          processing={!requestCompleted && busy}
          edit={edit}
          text={t(translations.Save)}
        />
      </Fragment>
    ),
  ] as ActionRenderer[];

  //RIGHT ACTIONS
  const rightActions = [
    () => (
      <Fragment>
        <Button
          variant="ghost"
          size="small"
          startIcon={<Icon icon="times" />}
          onClick={handleCancelClick}
        >
          {t(translations.Cancel)}
        </Button>
      </Fragment>
    ),
  ] as ActionRenderer[];

  //RENDER
  return (
    <>
      <PageWrapper
        pageName={
          edit ? t(translations.EditAttribute) : t(translations.AddNewAttribute)
        }
        titlePage={
          baseModel
            ? edit
              ? ` ${t(translations.EditAttribute)} #${queryParams.id || 'N/A'}`
              : ` ${t(translations.AddNewAttribute)}`
            : undefined
        }
        loading={loading}
        useSidePanel={useSidePanel}
        leftActions={leftActions}
        rightActions={rightActions}
        closable={true}
        disableExpandToggle={true}
        closeSidePanel={handleClose}
        pageLink={buildURL(
          publicUrl + 'UserAttributes/details',
          props.queryParams,
        )}
        children={
          baseModel !== undefined && !loading ? (
            <Fragment>
              <UserAttributesForm
                initialValues={baseModel || ({} as UserAttributesDetailsState)}
                bindSubmitForm={bindSubmitForm}
                onSubmit={handleSubmit}
                isEdit={edit}
                isAdmin={IsAdmin}
              />
            </Fragment>
          ) : (
            <Fragment>
              {' '}
              <Box component="div">
                <Progress inProgress={loading} size={80} />
              </Box>
            </Fragment>
          )
        }
      />
      {hasUnsavedChanges && (
        <Beforeunload onBeforeunload={() => 'Youll lose your data!'} />
      )}
    </>
  );
}
